import { lazy, Suspense, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileApp } from "@/components/mobile/MobileApp";
import { TopBar } from "@/components/TopBar";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { HomeHero } from "@/components/home/HomeHero";
import { CredibilityIntro, ProblemStatement, TwoPartPlatform } from "@/components/home/HomeStory";
import { ResuscitationLifecycle, CoreCapabilities } from "@/components/home/HomeLifecycle";
import { PersonalizationPrompt } from "@/components/personalization/PersonalizationPrompt";
import { PersonalizedGuide } from "@/components/personalization/PersonalizedGuide";
import { usePersonalization } from "@/lib/personalization";
import { orderSections, type SectionKey } from "@/content/personalization";
import { Seo } from "@/components/seo/Seo";
import { webSiteSchema } from "@/lib/seo";

// Below-the-fold sections are code-split so their JS is fetched after first paint.
const AudienceOutcomes = lazy(() =>
  import("@/components/home/HomeAudiences").then((m) => ({ default: m.AudienceOutcomes })),
);
const PlatformArchitecture = lazy(() =>
  import("@/components/home/HomeAudiences").then((m) => ({ default: m.PlatformArchitecture })),
);
const EvidenceTrust = lazy(() =>
  import("@/components/home/HomeProof").then((m) => ({ default: m.EvidenceTrust })),
);
const FeaturedResources = lazy(() =>
  import("@/components/home/HomeProof").then((m) => ({ default: m.FeaturedResources })),
);
const CategoryLeadership = lazy(() =>
  import("@/components/category/CategoryLeadership").then((m) => ({ default: m.CategoryLeadership })),
);
const MomentsSequence = lazy(() =>
  import("@/components/home/MomentsSequence").then((m) => ({ default: m.MomentsSequence })),
);
const FinalCta = lazy(() => import("@/components/home/HomeProof").then((m) => ({ default: m.FinalCta })));
const ComplianceSection = lazy(() =>
  import("@/components/ComplianceSection").then((m) => ({ default: m.ComplianceSection })),
);

/**
 * Homepage body sections keyed for optional reordering. Personalization only
 * changes the ORDER of this set — every section always renders, so content,
 * claims, notices, and crawlable text are identical in every variant.
 */
const SECTIONS: Record<SectionKey, JSX.Element> = {
  moments: <MomentsSequence key="moments" />,
  category: <CategoryLeadership key="category" />,
  lifecycle: <ResuscitationLifecycle key="lifecycle" />,
  capabilities: <CoreCapabilities key="capabilities" />,
  audiences: <AudienceOutcomes key="audiences" />,
  architecture: <PlatformArchitecture key="architecture" />,
  evidence: <EvidenceTrust key="evidence" />,
  resources: <FeaturedResources key="resources" />,
};

const Index = () => {
  const isMobile = useIsMobile();
  const { profile } = usePersonalization();
  const order = orderSections(profile);

  useEffect(() => {
    if (isMobile) return;
    // Content must be fully legible without animation.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "80px" },
    );

    document.querySelectorAll(".reveal").forEach((el) => {
      el.classList.add("opacity-0", "translate-y-6", "transition-all", "duration-700", "will-change-transform");
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [isMobile]);

  return (
    <div className="min-h-screen">
      <Seo
        title="Intelligent Resuscitation Infrastructure | datasyteAI™ + AiMediQ™"
        description="PathFree Technologies builds Intelligent Resuscitation Infrastructure: readiness, clinical response, documentation, data, and continuous improvement in one coordinated ecosystem."
        path="/"
        jsonLd={[webSiteSchema()]}
      />
      {isMobile ? (
        <MobileApp />
      ) : (
        <>
          <TopBar />
          <Header />
          <main>
            <HomeHero />
            <PersonalizationPrompt />
            <CredibilityIntro />
            <ProblemStatement />
            <TwoPartPlatform />
            <Suspense fallback={<div className="min-h-[40vh]" />}>
              <PersonalizedGuide />
              {order.map((key) => SECTIONS[key])}
              <FinalCta />
              <ComplianceSection />
            </Suspense>
          </main>
          <Footer />
        </>
      )}
    </div>
  );
};

export default Index;
